import {
  Button,
  Card,
  Col,
  Collapse,
  DatePicker,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Progress,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Switch,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useRequest } from 'ahooks';
import {
  CompanyDTO,
  CustomerAiProfileConfig,
  CustomerPoolEnum,
  CustomerStateEnum,
  systemController,
  userController,
} from '../../api';
import { useCompany } from '../../hooks/company';
import { PageHeader } from '@ant-design/pro-components';
import { PlayCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import MDEditor from '@uiw/react-md-editor';

// 以下字段尚未通过 NAD 同步到 api.ts，临时扩展类型
// 待后端部署后重新 yarn nad 即可移除
type CustomerAiProfileConfigExt = CustomerAiProfileConfig & {
  schemaDescriptions?: Record<string, string>;
  extraBody?: Record<string, unknown>;
  responseFormatType?: string;
};

const SCHEMA_FIELDS: { key: string; label: string }[] = [
  { key: 'star', label: '客户星级' },
  { key: 'state', label: '客户状态' },
  { key: 'reason', label: '判断理由' },
  { key: 'credit', label: '征信情况' },
  { key: 'is_native', label: '是否本地人' },
  { key: 'has_house', label: '是否有房' },
  { key: 'has_car', label: '是否有车' },
  { key: 'has_provident_fund', label: '是否有公积金' },
  { key: 'has_social_security', label: '是否有社保' },
  { key: 'has_business_license', label: '是否有营业执照' },
  { key: 'three_cert', label: '是否三证合一' },
];

const MarkdownField = ({
  value,
  onChange,
  height = 300,
}: {
  value?: string;
  onChange?: (val: string) => void;
  height?: number;
}) => (
  <div data-color-mode="light">
    <MDEditor
      value={value || ''}
      onChange={(val) => onChange?.(val || '')}
      height={height}
      preview="live"
    />
  </div>
);

const statusMap: Record<number, { text: string; color: string }> = {
  0: { text: '待处理', color: 'blue' },
  1: { text: '运行中', color: 'orange' },
  2: { text: '成功', color: 'green' },
  3: { text: '失败', color: 'red' },
};

const RunOneTask = ({ companies }: { companies?: CompanyDTO[] }) => {
  const [companyId, setCompanyId] = useState<string>();
  const [mobile, setMobile] = useState<string>();

  const { loading: runLoading, run: runOne } = useRequest(
    (cid: string, m: string) => systemController.runOneCustomerAiProfile(cid, m),
    {
      manual: true,
      onSuccess: () => message.success('任务已提交'),
      onError: (e) => message.error(e.message || '提交失败'),
    },
  );

  const { data: taskInfo, run: queryTask, loading: queryLoading } = useRequest(
    (cid: string, m: string) => systemController.getCustomerAiProfileTask(cid, m),
    { manual: true },
  );

  const handleRun = () => {
    if (!companyId || !mobile) {
      message.warning('请输入公司和手机号');
      return;
    }
    runOne(companyId, mobile);
  };

  const handleQuery = () => {
    if (!companyId || !mobile) {
      message.warning('请输入公司和手机号');
      return;
    }
    queryTask(companyId, mobile);
  };

  const status = taskInfo?.status != null ? statusMap[taskInfo.status] : null;

  return (
    <Card title="手动执行" size="small" style={{ marginBottom: 16 }}>
      <Row gutter={16} align="bottom">
        <Col span={6}>
          <Form.Item label="公司" style={{ marginBottom: 8 }}>
            <Select
              placeholder="选择公司"
              value={companyId}
              onChange={setCompanyId}
              showSearch
              optionFilterProp="label"
              options={companies?.map((c) => ({
                label: c.name,
                value: c.id?.toString(),
              }))}
            />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label="手机号" style={{ marginBottom: 8 }}>
            <Input
              placeholder="输入手机号"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
            />
          </Form.Item>
        </Col>
        <Col>
          <Form.Item style={{ marginBottom: 8 }}>
            <Space>
              <Button
                type="primary"
                icon={<PlayCircleOutlined />}
                onClick={handleRun}
                loading={runLoading}
              >
                开始
              </Button>
              <Button onClick={handleQuery} loading={queryLoading}>
                查询状态
              </Button>
            </Space>
          </Form.Item>
        </Col>
      </Row>
      {taskInfo && (
        <Descriptions size="small" column={4} bordered style={{ marginTop: 8 }}>
          <Descriptions.Item label="状态">
            <span style={{ color: status?.color }}>{status?.text}</span>
          </Descriptions.Item>
          <Descriptions.Item label="重试次数">
            {taskInfo.retryCount ?? 0} / {taskInfo.maxRetryCount ?? 0}
          </Descriptions.Item>
          <Descriptions.Item label="开始时间">
            {taskInfo.startedAt || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="完成时间">
            {taskInfo.finishedAt || '-'}
          </Descriptions.Item>
          {taskInfo.errorMessage && (
            <Descriptions.Item label="错误信息" span={4}>
              <span style={{ color: 'red' }}>{taskInfo.errorMessage}</span>
            </Descriptions.Item>
          )}
          {taskInfo.resultJson && (
            <Descriptions.Item label="结果" span={4}>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', maxHeight: 200, overflow: 'auto' }}>
                {taskInfo.resultJson}
              </pre>
            </Descriptions.Item>
          )}
        </Descriptions>
      )}
    </Card>
  );
};

const TaskProgress = () => {
  const { data, loading, refresh } = useRequest(
    () => systemController.getCustomerAiProfileTaskProgress(),
  );

  const titleExtra = (
    <Button
      size="small"
      icon={<ReloadOutlined />}
      onClick={refresh}
      loading={loading}
    >
      刷新
    </Button>
  );

  // 首次加载未返回数据时不渲染
  if (!data && loading) return <Card title="任务进度" size="small" style={{ marginBottom: 16 }} loading />;
  if (!data) return null;

  const total = Number(data.total) || 0;
  const success = Number(data.success) || 0;
  const failed = Number(data.failed) || 0;
  const running = Number(data.running) || 0;
  const pending = Number(data.pending) || 0;
  const percent = total > 0 ? Math.round(((success + failed) / total) * 100) : 0;

  return (
    <Card title="任务进度" size="small" style={{ marginBottom: 16 }} extra={titleExtra}>
      <Row gutter={16} align="middle">
        <Col span={4}>
          <Progress type="circle" percent={percent} size={80} />
        </Col>
        <Col span={4}>
          <Statistic title="总计" value={total} />
        </Col>
        <Col span={4}>
          <Statistic title="成功" value={success} valueStyle={{ color: '#3f8600' }} />
        </Col>
        <Col span={4}>
          <Statistic title="失败" value={failed} valueStyle={{ color: '#cf1322' }} />
        </Col>
        <Col span={4}>
          <Statistic title="运行中" value={running} valueStyle={{ color: '#fa8c16' }} />
        </Col>
        <Col span={4}>
          <Statistic title="待处理" value={pending} />
        </Col>
      </Row>
    </Card>
  );
};

export default function CustomerAiProfileConfigPage() {
  const [form] = Form.useForm<CustomerAiProfileConfigExt>();
  const [config, setConfig] = useState<CustomerAiProfileConfigExt>();

  const { loading: getLoading, refresh } = useRequest(
    () => systemController.getCustomerAiProfileConfig(),
    {
      onSuccess: (data) => {
        const v = data as CustomerAiProfileConfigExt;
        setConfig(v);
        form.setFieldsValue({
          ...v,
          filterCreatedAtStart: (v.filterCreatedAtStart ? dayjs(v.filterCreatedAtStart) : undefined) as any,
          filterCreatedAtEnd: (v.filterCreatedAtEnd ? dayjs(v.filterCreatedAtEnd) : undefined) as any,
          extraBodyJson: v.extraBody ? JSON.stringify(v.extraBody, null, 2) : undefined,
        } as any);
      },
    },
  );

  const { id: companyId } = useCompany();
  const { data: companies } = useRequest(() => systemController.listCompany());
  const { data: users } = useRequest(
    () => userController.listAllBaseUser(companyId),
    { refreshDeps: [companyId] },
  );

  const { loading: updateLoading, run: update } = useRequest(
    (v: CustomerAiProfileConfigExt) =>
      // Cast safe: NadInvoker serializes full runtime object, schemaDescriptions will be included in POST body
      systemController.updateCustomerAiProfileConfig(v as CustomerAiProfileConfig),
    {
      manual: true,
      onSuccess: () => {
        message.success('更新成功');
        refresh();
      },
      onError: (e) => {
        message.error(e.message || '更新失败');
      },
    },
  );

  const loading = getLoading || updateLoading;

  const handleSave = async () => {
    const values = await form.validateFields();

    let extraBody: Record<string, unknown> | undefined;
    const jsonStr = (values as any).extraBodyJson as string | undefined;
    if (jsonStr && jsonStr.trim()) {
      try {
        const parsed = JSON.parse(jsonStr) as unknown;
        if (parsed == null || Array.isArray(parsed) || typeof parsed !== 'object') {
          message.error('额外请求参数必须是 JSON 对象');
          return;
        }
        extraBody = parsed as Record<string, unknown>;
      } catch {
        message.error('额外请求参数 JSON 格式不正确');
        return;
      }
    }

    const { extraBodyJson: _, ...rest } = values as any;
    update({
      ...config,
      ...rest,
      extraBody,
      filterCreatedAtStart: values.filterCreatedAtStart
        ? (values.filterCreatedAtStart as unknown as dayjs.Dayjs).toISOString()
        : undefined,
      filterCreatedAtEnd: values.filterCreatedAtEnd
        ? (values.filterCreatedAtEnd as unknown as dayjs.Dayjs).toISOString()
        : undefined,
    });
  };

  const stateOptions = [
    { label: '待跟进', value: CustomerStateEnum.WAIT_FOLLOW_UP },
    { label: '资质不符合', value: CustomerStateEnum.UNQUALIFIED },
    { label: '待签约', value: CustomerStateEnum.WAIT_SIGN },
    { label: '已上门', value: CustomerStateEnum.VISITED },
    { label: '已签约', value: CustomerStateEnum.SIGNED },
    { label: '已放款', value: CustomerStateEnum.LOAN },
    { label: '捣乱申请', value: CustomerStateEnum.INVALID },
  ];

  const poolOptions = [
    { label: '业务池', value: CustomerPoolEnum.BIZ },
    { label: '公共池', value: CustomerPoolEnum.PUBLIC },
    { label: '仓库', value: CustomerPoolEnum.IDLE },
  ];

  return (
    <PageHeader title="AI 任务配置">
      <TaskProgress />
      <RunOneTask companies={companies} />

      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            temperature: 0.7,
            maxTokens: 1024,
            stopHour: 7,
            batchSize: 100,
            parallelism: 4,
            maxRetryCount: 3,
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="启用" name="enabled" valuePropName="checked">
                <Switch checkedChildren="开启" unCheckedChildren="关闭" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={6}>
              <Form.Item label="公司" name="filterCompanyIds">
                <Select
                  mode="multiple"
                  placeholder="不限"
                  showSearch
                  optionFilterProp="label"
                  options={companies?.map((c) => ({
                    label: c.name,
                    value: c.id,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="星级" name="filterStars">
                <Select
                  mode="multiple"
                  placeholder="不限"
                  options={[0, 1, 2, 3, 4, 5].map((s) => ({
                    label: `${s} 星`,
                    value: s,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="客户状态" name="filterStates">
                <Select
                  mode="multiple"
                  placeholder="不限"
                  options={stateOptions}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="客户池" name="filterPools">
                <Select
                  mode="multiple"
                  placeholder="不限"
                  options={poolOptions}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item label="所属员工" name="filterUserIds">
                <Select
                  mode="multiple"
                  placeholder="不限"
                  showSearch
                  optionFilterProp="label"
                  options={users?.map((u) => ({
                    label: u.name,
                    value: u.id,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="客户创建时间（起）" name="filterCreatedAtStart">
                <DatePicker
                  showTime
                  format="YYYY-MM-DD HH:mm:ss"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="客户创建时间（止）" name="filterCreatedAtEnd">
                <DatePicker
                  showTime
                  format="YYYY-MM-DD HH:mm:ss"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Collapse
            style={{ marginBottom: 24 }}
            items={[
              {
                key: 'llm',
                label: 'LLM 配置',
                forceRender: true,
                children: (
                  <>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item label="模型" name="model">
                          <Input placeholder="gpt-4.1-mini" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="API 地址" name="base_url">
                          <Input placeholder="https://api.openai.com/v1/chat/completions" />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item label="API Key" name="key">
                          <Input.Password placeholder="sk-..." />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item label="温度" name="temperature">
                          <InputNumber
                            min={0}
                            max={2}
                            step={0.1}
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item label="最大 Tokens" name="maxTokens">
                          <InputNumber min={1} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col span={6}>
                        <Form.Item
                          label="响应格式类型"
                          name="responseFormatType"
                          extra="不同 LLM 支持的类型不同，默认 json_schema"
                        >
                          <Select
                            allowClear
                            placeholder="json_schema（默认）"
                            options={[
                              { label: 'json_schema（结构化输出）', value: 'json_schema' },
                              { label: 'json_object（JSON 模式）', value: 'json_object' },
                            ]}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={18}>
                        <Form.Item
                          label="额外请求参数"
                          name="extraBodyJson"
                          extra="JSON 格式，如 {&quot;enable_thinking&quot;: false}，用于传递 LLM 特有参数"
                        >
                          <Input.TextArea
                            rows={2}
                            placeholder='{"enable_thinking": false}'
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </>
                ),
              },
              {
                key: 'prompt',
                label: 'Prompt 配置',
                forceRender: true,
                children: (
                  <>
                    <Form.Item label="System Prompt" name="systemPrompt">
                      <MarkdownField height={250} />
                    </Form.Item>
                    <Form.Item
                      label="Prompt 模板"
                      name="promptTemplate"
                      extra="可用变量：{{companyId}} {{mobile}} {{currentStar}} {{currentState}} {{customerRemark}} {{followUp}}"
                    >
                      <MarkdownField height={350} />
                    </Form.Item>
                    <Collapse
                      size="small"
                      style={{ marginTop: 16 }}
                      items={[
                        {
                          key: 'schema',
                          label: '输出字段描述（JSON Schema Description）',
                          children: (
                            <Row gutter={[16, 0]}>
                              {SCHEMA_FIELDS.map((f) => (
                                <Col span={12} key={f.key}>
                                  <Form.Item
                                    label={f.label}
                                    name={['schemaDescriptions', f.key]}
                                  >
                                    <Input.TextArea
                                      rows={2}
                                      placeholder="留空使用默认描述"
                                    />
                                  </Form.Item>
                                </Col>
                              ))}
                            </Row>
                          ),
                        },
                      ]}
                    />
                  </>
                ),
              },
              {
                key: 'task',
                label: '任务参数',
                forceRender: true,
                children: (
                  <Row gutter={16}>
                    <Col span={6}>
                      <Form.Item label="停止时间（小时）" name="stopHour">
                        <InputNumber
                          min={0}
                          max={23}
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item label="批次大小" name="batchSize">
                        <InputNumber min={1} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item label="并发数" name="parallelism">
                        <InputNumber
                          min={1}
                          max={32}
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item label="最大重试次数" name="maxRetryCount">
                        <InputNumber min={0} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                  </Row>
                ),
              },
            ]}
          />

          <Form.Item>
            <Button
              type="primary"
              onClick={handleSave}
              loading={updateLoading}
            >
              保存
            </Button>
          </Form.Item>
        </Form>
      </Spin>
    </PageHeader>
  );
}
